import {
  isPlatformBrowser,
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  PLATFORM_ID,
  signal
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { accountCountries } from '../../../../../core/helpers/country.helper';
import { Gender } from '../../../../../core/profile/gender';
import { MessagingType } from '../../../../../core/profile/messaging-type';
import { emulateClick } from '../../core/a11y';
import { Error } from '../../core/error';
import { DrawerService } from '../../drawer/drawer.service';
import {
  type Notification,
  NotificationService
} from '../../notification/notification.service';
import { AutoCloseDirective } from '../../shared/directives/auto-close.directive';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../shared/directives/progress-click.directive';
import { ByPassPipe } from '../../shared/pipes/by-pass.pipe';
import { AuthService } from '../../shared/services/auth.service';
import { PostalCodeService } from '../../shared/services/postal-code.service';
import { TelInputComponent } from '../../shared/components/tel-input/tel-input.component';
import { FrenchMobileValidator } from '../../shared/validators/validators';
import { CenterService } from '../../shared/services/center.service';
import {
  EmailValidator,
  PasswordValidator
} from '../../shared/validators/validators';
import { SignInComponent } from '../sign-in/sign-in.component';

type SignUpForm = {
  erpId: FormControl<number>;
  centerErpId: FormControl<number>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  address: FormControl<string>;
  additionalAddress: FormControl<string>;
  zip: FormControl<string>;
  city: FormControl<string>;
  country: FormControl<string>;
  phone: FormControl<string>;
  birthDay: FormControl<string>;
  birthMonth: FormControl<string>;
  birthYear: FormControl<string>;
  email: FormControl<string>;
  password: FormControl<string>;
  photoUrl: FormControl<string>;
  gender: FormControl<Gender>;
  emailConsent: FormControl<boolean | null>;
  smsConsent: FormControl<boolean | null>;
  courrierConsent: FormControl<boolean | null>;
};

@Component({
  selector: 'app-sign-up',
  imports: [
    TelInputComponent,
    ByPassPipe,
    HoverGradientDirective,
    NgOptimizedImage,
    NgxTolgeeModule,
    NgClass,
    ReactiveFormsModule,
    ProgressClickDirective,
    AutoCloseDirective,
  ],
  templateUrl: './sign-up.component.html'
})
export class SignUpComponent {
  readonly Gender = Gender;
  centerService = inject(CenterService);
  private platformId = inject(PLATFORM_ID);
  showPassword = signal<boolean>(false);
  acceptTerms = signal<boolean>(false);
  cities = signal<string[] | null>(null);
  days = Array(31).fill(undefined).map((_, i) => (i + 1).toLocaleString(undefined, { minimumIntegerDigits: 2 }));
  months = Array(12).fill(undefined).map((_, i) => (i + 1).toLocaleString(undefined, { minimumIntegerDigits: 2 }));
  years = Array(new Date().getFullYear() - 1900 + 1).fill(undefined).map((_, i) => String(i + 1900));
  private postalCodes = inject(PostalCodeService);
  form: FormGroup<SignUpForm> = new FormGroup<SignUpForm>({
    erpId: new FormControl(-1, { nonNullable: true }),
    centerErpId: new FormControl(this.centerService.center()?.erpId ?? -1, {
      nonNullable: true,
    }),
    firstName: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
    lastName: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
    address: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
    additionalAddress: new FormControl('', { nonNullable: true }),
    zip: new FormControl('', { nonNullable: true, validators: [ Validators.required, this.postalCodes.zipValidator ] }),
    city: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
    country: new FormControl('FRANCE', { nonNullable: true, validators: [ Validators.required ] }),
    phone: new FormControl('', {
      nonNullable: true,
      validators: [ Validators.required, FrenchMobileValidator ]
    }),
    birthDay: new FormControl('01', {
      nonNullable: true,
      validators: [ Validators.required ]
    }),
    birthMonth: new FormControl('01', {
      nonNullable: true,
      validators: [ Validators.required ]
    }),
    birthYear: new FormControl('1970', {
      nonNullable: true,
      validators: [ Validators.required ]
    }),
    photoUrl: new FormControl('', { nonNullable: true }),
    gender: new FormControl(Gender.Male, { nonNullable: true, validators: [ Validators.required ] }),
    email: new FormControl<string>('', { nonNullable: true, validators: [ Validators.required, EmailValidator ] }),
    password: new FormControl<string>(
      '',
      {
        nonNullable: true,
        validators: [ Validators.required, PasswordValidator ],
      }),
    emailConsent: new FormControl(null, { validators: [ Validators.required ] }),
    smsConsent: new FormControl(null, { validators: [ Validators.required ] }),
    courrierConsent: new FormControl(null, { validators: [ Validators.required ] })
  });
  activeCenters = computed(() => {
    return this.centerService.centers().filter(c => c.enabled && c.erpId > 0)
  });
  displayCities = computed(() => {
    const cities = this.cities();
    const current = this.form.get('city')?.value;
    return cities?.every(c => c !== current) ?? false;
  });
  protected readonly emulateClick = emulateClick;
  protected readonly sortedCountries = (): ReturnType<typeof accountCountries> => accountCountries('');
  private router = inject(Router);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private drawerService = inject(DrawerService);

  constructor() {
    effect(() => {
      if (!this.authService.initAuthProgress() && this.authService.user()?.authCompleted) {
        this.router.navigate([ '/' ], { replaceUrl: true }).catch(err => console.error(err))
      }
    });
    this.postalCodes.bind(this.form, this.cities);
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        const center = this.centerService.center();
        const ctrl = this.form.get('centerErpId');
        if (!ctrl) return;
        if (center?.erpId && center.erpId > 0) {
          ctrl.setValue(center.erpId);
          ctrl.clearValidators();
        } else {
          ctrl.setValidators([Validators.required, Validators.min(1)]);
        }
        ctrl.updateValueAndValidity();
      });
    }
  }

  get photoUrl(): string {
    return this.form.get('photoUrl')?.value ?? '';
  }

  hasCityList(): boolean {
    return this.postalCodes.hasCityList(this.form.get('country')?.value ?? '');
  }

  setCity(city: string): void {
    this.cities.set(null);
    this.form.get('city')?.setValue(city);
    this.form.updateValueAndValidity();
  }

  /**
   * Sign up the user
   * @returns The promise
   */
  signUp = async (): Promise<void> => {
    this.form.disable();
    const formData = this.form.getRawValue();
    try {
      const notification = [];
      if (formData.emailConsent) {
        notification.push(MessagingType.Email)
      }
      if (formData.smsConsent) {
        notification.push(MessagingType.SMS)
      }
      if (formData.courrierConsent) {
        notification.push(MessagingType.Courrier)
      }
      await this.authService.signUp({
        email: formData.email,
        password: formData.password,
        erpId: formData.erpId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        photoURL: formData.photoUrl,
        phone: formData.phone,
        address: formData.address,
        additionalAddress: formData.additionalAddress,
        zip: formData.zip,
        city: formData.city,
        country: formData.country,
        birthDate: `${formData.birthYear}-${formData.birthMonth}-${formData.birthDay}`,
        gender: formData.gender,
        notification,
        centerErpId: formData.centerErpId,
        shouldUpdateCenter: false,
        shouldUpdateConsent: false,
      });
      await this.authService.signOut(false);
      await this.drawerService.openAfterNavigation({
        component: SignInComponent,
        inputs: { inDrawer: true, login: formData.email, password: formData.password, autoLogin: true },
        style: 'transparent'
      }, [ '/' ]);
    } catch (err) {
      const error = err as Error;
      const notification: Notification = {
        type: 'error',
        title: error.details.code,
        message: error.message
      };
      if (error.details.code === 'auth/email-already-in-use') {
        notification.type = 'warning';
        notification.message = 'auth/email-already-in-use/description'
      }
      this.notificationService.open(notification);
    } finally {
      this.form.enable();
    }
  }

  /**
   * Set user profile image on signup
   */
  changeAvatar(): void {
    this.notificationService.open({ type: 'warning', title: 'notImplemented', message: 'notImplementedDescription' });
  }

  switchAcceptTerms(): void {
    this.acceptTerms.update(v => !v)
  }

  isValidDate(year: string, month: string, day: string): boolean {
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return date.getTime() <= Date.now();
  }
}
